/** Speed — line weight follows cursor speed: fast is thin, slow is thick. */
import type { CreateModule } from '../../core/module-api';
import { cfgNum } from '../../core/module-api';
import type { Point } from '../../core/scene';
import { defaultStyle } from '../../core/scene';
import { distance, strokeOutline } from '../../core/geometry';

const ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 16c4 0 5-8 9-8s3 5 7 5"/></svg>';

function widthFor(speed: number, max: number): number {
  const t = Math.max(0, Math.min(1, 1 - speed / 2.2));
  return Math.max(0.8, max * t);
}

export function createSpeed(): CreateModule {
  let pts: Point[] = [];
  let speeds: number[] = [];
  let drawing = false;

  return {
    manifest: {
      id: 'speed',
      kind: 'create',
      name: 'Speed',
      apiVersion: 1,
      description: 'Line weight follows your speed — fast is thin, slow is thick.',
      icon: ICON,
      params: [
        { kind: 'slider', key: 'max', label: 'Max', min: 2, max: 60, step: 1, default: 26 },
      ],
    },

    onPointerDown(s) {
      pts = [{ x: s.x, y: s.y }];
      speeds = [s.speed];
      drawing = true;
    },

    onPointerMove(s) {
      if (!drawing) return;
      const p = { x: s.x, y: s.y };
      const last = pts[pts.length - 1];
      if (!last || distance(last, p) >= 1.5) {
        pts.push(p);
        speeds.push(s.speed);
      }
    },

    onPointerUp() {
      drawing = false;
    },

    getLiveShapes(ctx) {
      if (pts.length === 0) return [];
      const max = cfgNum(ctx.config, 'max', 26);
      const widths = speeds.map((sp) => widthFor(sp, max));
      const style = defaultStyle();
      style.mode = 'fill';
      style.color = { ...ctx.ink };
      const shape = ctx.newShape({ path: strokeOutline(pts, widths), style });
      shape.variableWidth = { spine: pts.map((p) => ({ ...p })), width: widths };
      return [shape];
    },

    onClear() {
      pts = [];
      speeds = [];
    },

    onCommit() {
      pts = [];
      speeds = [];
    },
  };
}
